import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { ROLE_LABEL, ROLE_BADGE_CLS } from "../../lib/roleOptions";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Mail, Trash2, Inbox, Clock } from "lucide-react";
import { toast } from "sonner";
import {
  diffDaysIST, formatISTDate, formatISTTime, istDayKey, todayIST, NO_VALUE,
} from "../../lib/datetime";
import { joinParts, plural } from "../../lib/vocabulary";

/**
 * When this invite dies, in IST, with the date it dies on.
 *
 * WHAT WAS WRONG WITH THE OLD ONE (RC-8/RC-14). It subtracted two instants and
 * rounded the milliseconds to days, so "Expires in 1d" could mean anything from
 * 13 to 36 hours, and a link expiring at 00:30 IST — stored 19:00Z the previous
 * day — counted from the wrong day entirely for anyone whose phone was not on
 * IST. It also rendered a bare `1d` with no zone and no date, so the one thing
 * a person needs in order to act ("expires when?") was never on screen. Day
 * arithmetic now runs on IST calendar days, and the date is shown.
 */
function relativeExpiry(iso) {
  const day = istDayKey(iso);
  if (!day) return NO_VALUE;
  const today = todayIST();
  if (day === today) return joinParts([`Expires today`, formatISTTime(iso)]);
  // `diffDaysIST` is inclusive of both ends, so a gap of one calendar day
  // comes back as 2 — the "-1" turns it into days from now.
  if (day < today) {
    return joinParts([`Expired ${plural(diffDaysIST(day, today) - 1, "day")} ago`, formatISTDate(iso)]);
  }
  return joinParts([`Expires in ${plural(diffDaysIST(today, day) - 1, "day")}`, formatISTDate(iso)]);
}

export default function InvitesPanel({ onChanged }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/invites");
      setInvites(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRevoke() {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await api.delete(`/invites/${encodeURIComponent(revokeTarget.email)}`);
      toast.success("Invite revoked");
      setRevokeTarget(null);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRevoking(false);
    }
  }

  const pending = invites.filter((i) => !i.used_at);
  const used = invites.filter((i) => i.used_at);

  return (
    <div className="space-y-4" data-testid="invites-panel">
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      ) : pending.length === 0 ? (
        <div className="bg-white border border-callisto rounded-xl p-10 flex flex-col items-center text-center" data-testid="invites-empty">
          <div className="h-14 w-14 rounded-xl bg-callisto-navy/5 flex items-center justify-center mb-3">
            <Inbox className="h-6 w-6 text-callisto-navy" />
          </div>
          <p className="text-sm font-semibold text-callisto-navy">No pending invites</p>
          <p className="mt-1 text-xs text-callisto-muted">Use the "Invite user" button at the top to add a new teammate.</p>
        </div>
      ) : (
        <section>
          <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
            Pending · {pending.length}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="invites-pending-grid">
            {pending.map((inv) => (
              <div
                key={inv.email}
                className="bg-white border border-callisto rounded-xl p-4 flex flex-col gap-2"
                data-testid={`invite-card-${inv.email}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-callisto-navy flex items-center gap-1.5 truncate">
                      <Mail size={13} className="text-callisto-gold shrink-0" />
                      <span className="truncate">{inv.email}</span>
                    </p>
                    <p className="text-[11px] text-callisto-muted mt-0.5 flex items-center gap-1">
                      <Clock size={11} /> {relativeExpiry(inv.expires_at)}
                    </p>
                  </div>
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 shrink-0 ${ROLE_BADGE_CLS[inv.role] || "bg-slate-100 text-slate-700"}`}
                  >
                    {ROLE_LABEL[inv.role] || inv.role}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRevokeTarget(inv)}
                  className="h-8 border-red-200 text-red-700 hover:bg-red-50 text-[11px] font-semibold mt-1"
                  data-testid={`invite-revoke-${inv.email}`}
                >
                  <Trash2 size={12} className="mr-1" /> Revoke
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      {used.length > 0 && (
        <section>
          <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
            Accepted · {used.length}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {used.map((inv) => (
              <div
                key={inv.email}
                className="bg-white border border-callisto rounded-xl p-4 opacity-70"
                data-testid={`invite-accepted-${inv.email}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-callisto-navy truncate flex items-center gap-1.5">
                    <Mail size={13} className="text-callisto-muted" />
                    <span className="truncate">{inv.email}</span>
                  </p>
                  <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-700 shrink-0">
                    Joined
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this invite?</AlertDialogTitle>
            <AlertDialogDescription>
              The invite link for{" "}
              <span className="font-semibold text-callisto-navy">{revokeTarget?.email}</span> will be
              invalidated immediately. You can always send a fresh invite afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              disabled={revoking}
              className="bg-red-600 hover:bg-red-700"
              data-testid="invite-revoke-confirm"
            >
              {revoking ? "Revoking…" : "Revoke"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
